import { useEffect, useRef, useState } from 'react'
import { Users } from 'lucide-react'
import type { ProductAudience } from '@/lib/productAudience'
import {
  productAudienceDescription,
  productAudienceLabel,
} from '@/lib/productAudience'
import { cn } from '@/lib/utils'

interface ProductAudienceBadgeProps {
  audience: ProductAudience
  className?: string
  /** Pin to product image — popup stays inside image bounds. */
  placement?: 'default' | 'on-image'
}

function AudienceSymbol({ audience }: { audience: ProductAudience }) {
  if (audience === 'both') {
    return <Users className="h-3.5 w-3.5" strokeWidth={2.25} />
  }

  return (
    <span className="text-xl font-semibold leading-none" aria-hidden>
      {audience === 'men' ? '♂' : '♀'}
    </span>
  )
}

/** Storefront badge for optional men / women / both targeting. */
export function ProductAudienceBadge({
  audience,
  className,
  placement = 'default',
}: ProductAudienceBadgeProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const label = productAudienceLabel(audience)
  const description = productAudienceDescription(audience)
  const onImage = placement === 'on-image'

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return

    const timer = window.setTimeout(() => setOpen(false), 3000)
    return () => window.clearTimeout(timer)
  }, [open])

  const toggleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setOpen((current) => !current)
  }

  const button = (
    <button
      type="button"
      aria-label={label}
      aria-expanded={open}
      onClick={toggleOpen}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-gold-200 backdrop-blur-sm border border-white/10 transition-colors hover:bg-black/65 hover:text-gold-100',
        open && 'bg-black/70 text-gold-100 ring-1 ring-gold-500/40'
      )}
    >
      <AudienceSymbol audience={audience} />
    </button>
  )

  const message = (
    <>
      <p className="text-xs font-medium text-gold-300">{label}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-zinc-400">
        {description}
      </p>
    </>
  )

  if (onImage) {
    return (
      <div ref={rootRef} className={cn('relative pointer-events-auto', className)}>
        {button}

        {open && (
          <div
            role="tooltip"
            className="absolute bottom-full left-0 z-30 mb-2 w-44 max-w-[min(11rem,calc(100vw-2rem))] rounded-xl border border-gold-500/25 bg-luxury-950/95 px-3 py-2.5 text-left shadow-xl backdrop-blur-sm"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
          >
            {message}
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {button}

      {open && (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-20 mt-2 w-44 rounded-xl border border-gold-500/25 bg-luxury-950/95 px-3 py-2.5 text-left shadow-xl backdrop-blur-sm"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          {message}
        </div>
      )}
    </div>
  )
}
